import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { SKIP_AUTH, SKIP_OFFLINE_REPORT } from '../interceptors/http-context.tokens';

export const HEALTH_PROBE_INTERVAL_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private readonly http = inject(HttpClient);

  readonly online = signal(true);

  private timer: ReturnType<typeof setInterval> | null = null;
  private probing = false;

  // Listeners are wired here rather than from an init() call: `TranslationService`
  // is an eager bootstrap provider whose constructor fires HTTP before
  // AppComponent exists, so the first failure can arrive before any ngOnInit.
  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('offline', () => this.goOffline());
    window.addEventListener('online', () => void this.probe());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this.online()) {
        void this.probe();
      }
    });
  }

  reportReachable(): void {
    this.online.set(true);
    this.stopProbing();
  }

  // A status-0 error is not proof of an outage: an XHR aborted by navigation
  // looks identical. Confirm with one probe before showing the banner,
  // otherwise it flickers during normal service on the polling screens.
  reportSuspectedOutage(): void {
    if (!this.online() || this.timer || this.probing) {
      return;
    }
    void this.probe();
  }

  private goOffline(): void {
    this.online.set(false);
    this.startProbing();
  }

  private async probe(): Promise<void> {
    if (this.probing) {
      return;
    }
    this.probing = true;
    try {
      await firstValueFrom(
        this.http.get(`${environment.apiUrl}/ping`, {
          context: new HttpContext().set(SKIP_AUTH, true).set(SKIP_OFFLINE_REPORT, true),
          responseType: 'text',
        })
      );
      this.reportReachable();
    } catch {
      this.goOffline();
    } finally {
      this.probing = false;
    }
  }

  private startProbing(): void {
    if (this.timer) {
      return;
    }
    this.timer = setInterval(() => void this.probe(), HEALTH_PROBE_INTERVAL_MS);
  }

  private stopProbing(): void {
    if (!this.timer) {
      return;
    }
    clearInterval(this.timer);
    this.timer = null;
  }
}
